import { runCvImportWithReview } from './cv-import-review';
import { promptCvImportReview } from './cv-import-review-prompt';
import { api } from "./api";
import { createImageUploadUrl as createSharedImageUploadUrl } from "./media-api";
import type {
  APIResponse,
  CvParseQuality,
  ParsedCvData,
  UsernameCheckResult,
  OnboardingState,
  OnboardingCompleteResult,
  OnboardingIntent,
  ImageUploadKind,
  CreateImageUploadUrlResult,
  AISuggestSkillsResult,
  AIGenerateHighlightsResult,
  AIEnhanceTextResult,
  OnboardingCompanyProfile,
  SaveOnboardingCompanyInput,
  SaveOnboardingCompanyResult,
  AIDraftExperienceInput,
  AIDraftExperienceResult,
  AIDraftEducationInput,
  AIDraftEducationResult,
  OnboardingPreferencesInput,
  OnboardingPreferencesResult,
  OnboardingEnrichCvResult,
} from "@tabbio/types";

export const MIN_RESUME_TEXT_PARSE_LENGTH = 10;

export class OnboardingApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly meta?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "OnboardingApiError";
  }
}

/**
 * Parse failures the server reports as recoverable (incident 2026-09-02): the
 * document was read but holds no usable CV body, or the AI provider refused
 * the extraction. Nothing was written; the user retries, uploads another
 * file, or reviews the extracted text. Never a reason to replace a profile.
 */
export const RECOVERABLE_CV_PARSE_ERROR_CODES = new Set([
  "CV_PARSE_INCOMPLETE",
  "CV_PROVIDER_UNAVAILABLE",
]);

/** The server refused a replacement that would have emptied a populated CV. */
export const CV_REPLACEMENT_INCOMPLETE_CODE = "CV_REPLACEMENT_INCOMPLETE";

const EXPECTED_ONBOARDING_ERROR_CODES = new Set([
  "VALIDATION_ERROR",
  "MISSING_FILE",
  "FILE_TOO_LARGE",
  "REQUEST_ABORTED",
  "UNREADABLE_FILE",
  "UNSUPPORTED_FILE_TYPE",
  "VISION_EXTRACT_FAILED",
  "WORD_EXTRACT_FAILED",
  ...RECOVERABLE_CV_PARSE_ERROR_CODES,
  CV_REPLACEMENT_INCOMPLETE_CODE,
  "CV_IMPORT_REVIEW_REQUIRED",
  "CV_IMPORT_REVIEW_CANCELLED",
  "CV_IMPORT_SOURCE_CHANGED",
]);

const UNREADABLE_RESUME_FILE_CODES = new Set([
  "UNREADABLE_FILE",
  "UNSUPPORTED_FILE_TYPE",
  "VISION_EXTRACT_FAILED",
  "WORD_EXTRACT_FAILED",
]);

// Legacy substring fallbacks: pre-2026-05-25 backends collapsed all parse
// failures into a single PARSE_FAILED code. The current backend emits the
// distinct codes above; these substrings remain a transition safety net for
// in-flight events from older builds.
const LEGACY_RESUME_PARSE_FAILURE_MESSAGES = [
  "could not extract meaningful text from file",
  "could not extract content from file",
  "could not extract text from word document",
  "unsupported file type",
];

export function isExpectedOnboardingApiError(error: unknown) {
  if (!(error instanceof OnboardingApiError)) {
    return false;
  }

  if (EXPECTED_ONBOARDING_ERROR_CODES.has(error.code)) {
    return true;
  }

  const message = error.message.toLowerCase();
  return (
    error.code === "PARSE_FAILED" &&
    LEGACY_RESUME_PARSE_FAILURE_MESSAGES.some((expectedMessage) =>
      message.includes(expectedMessage),
    )
  );
}

export function isUnreadableResumeFileError(error: unknown) {
  if (!(error instanceof OnboardingApiError)) {
    return false;
  }

  if (UNREADABLE_RESUME_FILE_CODES.has(error.code)) {
    return true;
  }

  const message = error.message.toLowerCase();
  return (
    error.code === "PARSE_FAILED" &&
    (message.includes("could not extract meaningful text from file") ||
      message.includes("could not extract content from file") ||
      message.includes("could not extract text from word document"))
  );
}

export type CvParseRecovery = {
  kind: "incomplete" | "provider_unavailable";
  /** Privacy-safe parse report the server attached, when it did. */
  quality: CvParseQuality | null;
  /** The exact text the server read, for an editable manual review. */
  extractedText: string | null;
};

/**
 * A parse the server rejected as recoverable. The onboarding and resume
 * import composers show a specific message for it and can hand the extracted
 * text back to the user for review instead of a dead end.
 */
export function getCvParseRecovery(error: unknown): CvParseRecovery | null {
  if (
    !(error instanceof OnboardingApiError) ||
    !RECOVERABLE_CV_PARSE_ERROR_CODES.has(error.code)
  ) {
    return null;
  }
  const meta = error.meta ?? {};
  const quality =
    meta.quality && typeof meta.quality === "object"
      ? (meta.quality as CvParseQuality)
      : null;
  const extractedText =
    typeof meta.extractedText === "string" && meta.extractedText.trim()
      ? meta.extractedText
      : null;
  return {
    kind:
      error.code === "CV_PROVIDER_UNAVAILABLE"
        ? "provider_unavailable"
        : "incomplete",
    quality,
    extractedText,
  };
}

export function isCvReplacementIncompleteError(error: unknown) {
  return (
    error instanceof OnboardingApiError &&
    error.code === CV_REPLACEMENT_INCOMPLETE_CODE
  );
}

/**
 * Replace CV can surface validation guidance returned by the API because it is
 * actionable user input feedback. Other failures keep the translated generic
 * fallback so transport or server details are not exposed in the UI.
 */
export function getOnboardingValidationMessage(error: unknown) {
  if (
    !(error instanceof OnboardingApiError) ||
    error.code !== "VALIDATION_ERROR" ||
    !error.message.trim()
  ) {
    return null;
  }

  return error.message;
}

function getOnboardingApiError<T>(response: APIResponse<T>) {
  if (!response.error) {
    return null;
  }

  return new OnboardingApiError(
    response.error.message,
    response.error.code,
    response.meta as Record<string, unknown> | undefined,
  );
}

export function getResumeTextValidationError(text: string) {
  return text.trim().length < MIN_RESUME_TEXT_PARSE_LENGTH
    ? "Please provide more detail"
    : null;
}

// ─── Username ──────────────────────────────────────────────────

export async function checkUsernameAvailability(
  username: string,
): Promise<UsernameCheckResult> {
  const res = await api.post<UsernameCheckResult>(
    "/api/onboarding/check-username",
    {
      username,
    },
  );
  if (res.error) throw new Error(res.error.message);
  return res.data ?? { available: false };
}

export async function claimUsername(
  username: string,
): Promise<{ username: string }> {
  const res = await api.post<{ username: string }>(
    "/api/onboarding/claim-username",
    {
      username,
    },
  );
  if (res.error) throw new Error(res.error.message);
  return res.data ?? ({} as any);
}

// ─── Profile Setup ─────────────────────────────────────────────

export async function saveProfileSetup(data: {
  name: string;
  username?: string;
  nationality?: string;
  phone?: string;
  avatarUrl?: string | null;
}): Promise<{ profile: unknown; username?: string | null }> {
  const res = await api.post<{ profile: unknown; username?: string | null }>(
    "/api/onboarding/profile-setup",
    data,
  );
  if (res.error) throw new Error(res.error.message);
  return res.data ?? ({} as any);
}

// ─── Intent ────────────────────────────────────────────────────

export async function setIntent(
  intent: OnboardingIntent,
): Promise<{ intent: OnboardingIntent }> {
  const res = await api.post<{ intent: OnboardingIntent }>(
    "/api/onboarding/set-intent",
    { intent },
  );
  if (res.error) throw new Error(res.error.message);
  return res.data ?? ({} as any);
}

// ─── Preferences ───────────────────────────────────────────────

export async function saveOnboardingPreferences(
  data: OnboardingPreferencesInput,
): Promise<OnboardingPreferencesResult> {
  const res = await api.put<OnboardingPreferencesResult>(
    "/api/onboarding/preferences",
    data,
  );
  if (res.error) throw new Error(res.error.message);
  return res.data ?? ({} as any);
}

// ─── Company Setup ─────────────────────────────────────────────

export async function saveOnboardingCompany(
  data: SaveOnboardingCompanyInput,
): Promise<SaveOnboardingCompanyResult> {
  const res = await api.post<SaveOnboardingCompanyResult>(
    "/api/onboarding/company",
    data,
  );
  if (res.error) throw new Error(res.error.message);
  return res.data ?? ({} as any);
}

export async function getOnboardingCompany(): Promise<OnboardingCompanyProfile | null> {
  const res = await api.get<{ company: OnboardingCompanyProfile | null }>(
    "/api/onboarding/company",
  );
  if (res.error) throw new Error(res.error.message);
  return res.data?.company ?? null;
}

// ─── Upload (company logo) ────────────────────────────────────

export async function createAssetUploadUrl(params: {
  kind: ImageUploadKind;
  fileName: string;
  mimeType: string;
  companyId?: string;
}): Promise<CreateImageUploadUrlResult> {
  return createSharedImageUploadUrl(params);
}

// ─── CV Parsing ────────────────────────────────────────────────

export type ParseCvResult = {
  parsed: ParsedCvData;
  /** How the CV was parsed; absent from pre-incident backends. */
  quality?: CvParseQuality;
  importEvidenceId?: string;
};

export async function parseCvFile(
  formData: FormData,
  opts?: { skipEnrichment?: boolean },
): Promise<ParseCvResult> {
  const url = opts?.skipEnrichment
    ? "/api/onboarding/parse-cv?raw=1"
    : "/api/onboarding/parse-cv";
  const json = await api.postForm<ParseCvResult>(url, formData);
  const error = getOnboardingApiError(json);
  if (error) throw error;
  if (!json.data) {
    throw new OnboardingApiError("Could not parse CV", "PARSER_RUNTIME_ERROR");
  }
  return json.data;
}

export async function parseCvText(
  text: string,
  opts?: { skipEnrichment?: boolean },
): Promise<ParseCvResult> {
  const validationError = getResumeTextValidationError(text);
  if (validationError) {
    throw new OnboardingApiError(validationError, "VALIDATION_ERROR");
  }

  const url = opts?.skipEnrichment
    ? "/api/onboarding/generate-profile?raw=1"
    : "/api/onboarding/generate-profile";
  const res = await api.post<ParseCvResult>(url, {
    description: text,
  });
  const error = getOnboardingApiError(res);
  if (error) throw error;
  if (!res.data?.parsed) {
    throw new OnboardingApiError("Could not parse CV", "PARSER_RUNTIME_ERROR");
  }
  return res.data;
}

// ─── Document Text Extraction ──────────────────────────────────

/** Server cap for POST /api/onboarding/extract-text uploads. */
export const MAX_EXTRACT_FILE_BYTES = 10 * 1024 * 1024;

export type ExtractableFile = {
  name: string;
  /** Native picker asset URI; ignored when `file` is set. */
  uri?: string;
  type?: string | null;
  /** Web: the actual File/Blob (RN's FormData cannot read web picker URIs). */
  file?: Blob;
};

export async function extractDocumentText(
  file: ExtractableFile,
): Promise<{ text: string }> {
  const formData = new FormData();
  if (file.file) {
    formData.append("file", file.file, file.name);
  } else {
    formData.append("file", {
      uri: file.uri,
      name: file.name,
      type: file.type || "application/octet-stream",
    } as any);
  }
  const json = await api.postForm<{ text: string }>(
    "/api/onboarding/extract-text",
    formData,
  );
  const error = getOnboardingApiError(json);
  if (error) throw error;
  if (!json.data?.text?.trim()) {
    throw new OnboardingApiError(
      "Could not extract text from file",
      "UNREADABLE_FILE",
    );
  }
  return json.data;
}

// ─── Enrich CV (post choose-ai; tier + style aware) ────────────

export async function enrichCv(
  params: { styleSegmentId?: string } = {},
): Promise<OnboardingEnrichCvResult> {
  const res = await api.post<OnboardingEnrichCvResult>(
    "/api/onboarding/enrich-cv",
    params,
  );
  const error = getOnboardingApiError(res);
  if (error) throw error;
  if (!res.data?.enriched) {
    throw new OnboardingApiError("Could not enrich CV", "ENRICH_FAILED");
  }
  return res.data;
}

// ─── Save Sections ─────────────────────────────────────────────

export async function saveSections(
  data: Record<string, unknown>,
): Promise<{ saved: Record<string, unknown> }> {
  const write = async (payload: Record<string, unknown>) => {
    const res = await api.post<{ saved: Record<string, unknown> }>(
      "/api/onboarding/save-sections", payload,
    );
    const error = getOnboardingApiError(res);
    if (error) throw error;
    return res.data ?? ({} as { saved: Record<string, unknown> });
  };
  return data.source || data.importEvidenceId
    ? runCvImportWithReview(data, write, promptCvImportReview)
    : write(data);
}

// ─── Complete Onboarding ───────────────────────────────────────

export async function completeOnboarding(): Promise<OnboardingCompleteResult> {
  const res = await api.post<OnboardingCompleteResult>(
    "/api/onboarding/complete",
    {},
  );
  if (res.error) throw new Error(res.error.message);
  return res.data ?? ({} as any);
}

// Hiring hatch (employer Phase 1): completes onboarding WITHOUT creating a
// starter CV or publishing the personal profile. Separate function so the
// seeker completion path stays untouched.
export async function completeOnboardingHiring(): Promise<OnboardingCompleteResult> {
  const res = await api.post<OnboardingCompleteResult>(
    "/api/onboarding/complete",
    { hiring: true },
  );
  if (res.error) throw new Error(res.error.message);
  return res.data ?? ({} as any);
}

// ─── Get Onboarding State ──────────────────────────────────────

export async function getOnboardingState(): Promise<OnboardingState> {
  const res = await api.get<OnboardingState>("/api/onboarding/state");
  if (res.error) throw new Error(res.error.message);
  return res.data ?? ({} as any);
}

// ─── Get Full Onboarding Profile (for resume on refresh) ───────

export async function getOnboardingProfile(): Promise<Record<string, any>> {
  const res = await api.get<Record<string, any>>("/api/onboarding/profile");
  if (res.error) throw new Error(res.error.message);
  return res.data ?? ({} as any);
}

// ─── AI: Suggest Skills ────────────────────────────────────────

export async function suggestSkills(params: {
  title?: string;
  experience?: {
    title: string;
    company: string;
    description?: string;
    achievements?: string[];
  }[];
  education?: { school: string; degree?: string; fieldOfStudy?: string }[];
  existingSkills?: string[];
}): Promise<AISuggestSkillsResult> {
  const res = await api.post<AISuggestSkillsResult>(
    "/api/onboarding/ai/suggest-skills",
    params,
  );
  if (res.error) throw new Error(res.error.message);
  return res.data ?? ({} as any);
}

// ─── AI: Suggest Job Titles ────────────────────────────────────

export async function suggestJobTitles(params: {
  currentTitle?: string;
  experience?: {
    title: string;
    company: string;
    description?: string;
    achievements?: string[];
    current?: boolean;
  }[];
  topSkills?: string[];
  existingTitles?: string[];
}): Promise<{ titles: string[] }> {
  const res = await api.post<{ titles: string[] }>(
    "/api/onboarding/ai/suggest-job-titles",
    params,
  );
  if (res.error) throw new Error(res.error.message);
  return res.data ?? { titles: [] };
}

// ─── AI: Generate Highlights ───────────────────────────────────

export async function generateHighlights(params: {
  name?: string;
  title?: string;
  experience?: { title: string; company: string; achievements: string[] }[];
  certifications?: { name: string; issuer: string }[];
  skills?: string[];
  existingHighlights?: { title: string; description: string }[];
}): Promise<AIGenerateHighlightsResult> {
  const res = await api.post<AIGenerateHighlightsResult>(
    "/api/onboarding/ai/generate-highlights",
    params,
  );
  if (res.error) throw new Error(res.error.message);
  return res.data ?? ({} as any);
}

// ─── AI: Generate Summary ─────────────────────────────────────

export async function generateSummary(params: {
  name?: string;
  title?: string;
  experience?: {
    title: string;
    company: string;
    startDate?: string;
    endDate?: string;
    current?: boolean;
    employmentType?: string;
    location?: string;
    locationType?: string;
    description?: string;
    achievements?: string[];
  }[];
  education?: { school: string; degree?: string; fieldOfStudy?: string }[];
  certifications?: { name: string; issuer: string }[];
  skills?: string[];
  careerHighlights?: { title: string; description: string }[];
}): Promise<{ summary: string }> {
  const res = await api.post<{ summary: string }>(
    "/api/onboarding/ai/generate-summary",
    params,
  );
  if (res.error) throw new Error(res.error.message);
  return res.data ?? ({} as any);
}

// ─── AI: Draft Experience / Education ─────────────────────────

export async function draftExperience(
  params: AIDraftExperienceInput,
): Promise<AIDraftExperienceResult> {
  const res = await api.post<AIDraftExperienceResult>(
    "/api/onboarding/ai/draft-experience",
    params,
  );
  if (res.error) throw new Error(res.error.message);
  return res.data ?? ({} as any);
}

export async function draftEducation(
  params: AIDraftEducationInput,
): Promise<AIDraftEducationResult> {
  const res = await api.post<AIDraftEducationResult>(
    "/api/onboarding/ai/draft-education",
    params,
  );
  if (res.error) throw new Error(res.error.message);
  return res.data ?? ({} as any);
}

// ─── AI: Generate Title ───────────────────────────────────────

export async function generateTitle(params: {
  latestJobTitle?: string;
  totalYears?: number;
  topSkills?: string[];
  industry?: string;
  summary?: string;
  isCareerChanger?: boolean;
  targetRole?: string;
  name?: string;
  experience?: {
    title?: string;
    company?: string;
    startDate?: string;
    endDate?: string;
    current?: boolean;
    employmentType?: string;
    location?: string;
    locationType?: string;
    achievements?: string[];
  }[];
  education?: {
    school?: string;
    degree?: string;
    fieldOfStudy?: string;
  }[];
  certifications?: { name?: string; issuer?: string }[];
  careerHighlights?: { title?: string; description?: string }[];
  languages?: string[];
}): Promise<{ titleOptions: string[] }> {
  const res = await api.post<{ titleOptions: string[] }>(
    "/api/onboarding/ai/generate-title",
    params,
  );
  if (res.error) throw new Error(res.error.message);
  return res.data ?? ({} as any);
}

// ─── AI: Enhance Text ──────────────────────────────────────────

export async function enhanceText(params: {
  text: string;
  field: string;
  scope?: "section" | "full_cv" | "apply_skill";
  context?: { title?: string; company?: string; skills?: string[] };
}): Promise<AIEnhanceTextResult> {
  const res = await api.post<AIEnhanceTextResult>(
    "/api/onboarding/ai/enhance-text",
    params,
  );
  if (res.error) throw new Error(res.error.message);
  return res.data ?? ({} as any);
}

// ─── Resume Source ─────────────────────────────────────────────

export type ResumeSourceData = {
  id: string;
  userId: string;
  raw: ParsedCvData;
  previousRaw?: ParsedCvData | null;
  importedAt: string;
  previousCapturedAt?: string | null;
  canRevert?: boolean;
};

export type RevertToSourceResult = {
  success: true;
  restored: ParsedCvData;
};

export async function getResumeSource(): Promise<ResumeSourceData | null> {
  const res = await api.get<{ source: ResumeSourceData | null }>(
    "/api/onboarding/resume-source",
  );
  const error = getOnboardingApiError(res);
  if (error) throw error;
  const source = res.data?.source ?? null;
  return source
    ? {
        ...source,
        canRevert: source.canRevert ?? Boolean(source.previousRaw),
      }
    : null;
}

export async function revertToSource(): Promise<RevertToSourceResult> {
  const res = await api.post<RevertToSourceResult>(
    "/api/onboarding/revert-to-source",
    {},
  );
  const error = getOnboardingApiError(res);
  if (error) throw error;
  if (!res.data?.restored) {
    throw new OnboardingApiError(
      "Revert failed: unexpected empty response",
      "REVERT_FAILED",
    );
  }
  return res.data;
}
